import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkflowGraphStore } from '../../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../../store/workflow-editor.service';
import { WorkflowGraphNode } from '../../../models/flow-graph.model';
import { WORKFLOW_NODE_TYPE_META, getWorkflowActionCategory } from '../../../models/node-catalog';
import { ToastService } from '../../../../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../../../../shared/services/confirm-dialog.service';

import { WorkflowTriggerConfigComponent } from '../node-config/trigger-config.component';
import { WorkflowConditionConfigComponent } from '../node-config/condition-config.component';
import { WorkflowWaitConfigComponent } from '../node-config/wait-config.component';
import { WorkflowAnnotationConfigComponent } from '../node-config/annotation-config.component';
import { WorkflowCommunicationActionConfigComponent } from '../node-config/communication-action-config.component';
import { WorkflowTaskActivityActionConfigComponent } from '../node-config/task-activity-action-config.component';
import { WorkflowPipelineActionConfigComponent } from '../node-config/pipeline-action-config.component';
import { WorkflowTicketActionConfigComponent } from '../node-config/ticket-action-config.component';
import { WorkflowWebhookActionConfigComponent } from '../node-config/webhook-action-config.component';
import { WorkflowCodeIntegrationActionConfigComponent } from '../node-config/code-integration-action-config.component';
import { WorkflowCrmActionConfigComponent } from '../node-config/crm-action-config.component';
import { WorkflowEdgeConfigComponent } from '../edge-config/edge-config.component';

/**
 * Host del inspector (Fase 3).
 *
 * Sustituye al puente provisional de Fase 2 (que reusaba el `workflow-node-config-panel`
 * monolítico + `store.applyServerNode`) por un `switch` de tipo → un componente pequeño por
 * subtipo, calcado del inspector de chatflow. El host aporta el cromo común (cabecera,
 * nombre, activo, escape a JSON crudo) y delega la config concreta al componente del subtipo.
 *
 * Flujo de guardado DEFINITIVO: la config se edita en el STORE y se persiste por el PUT
 * atómico del grafo (autosave), no contra los endpoints por-categoría del panel viejo.
 *
 * Reactividad SOLO por signals del store/editor (gotcha §6). OnPush.
 */
@Component({
  selector: 'app-workflow-inspector-host',
  standalone: true,
  imports: [
    CommonModule,
    WorkflowTriggerConfigComponent,
    WorkflowConditionConfigComponent,
    WorkflowWaitConfigComponent,
    WorkflowAnnotationConfigComponent,
    WorkflowCommunicationActionConfigComponent,
    WorkflowTaskActivityActionConfigComponent,
    WorkflowPipelineActionConfigComponent,
    WorkflowTicketActionConfigComponent,
    WorkflowWebhookActionConfigComponent,
    WorkflowCodeIntegrationActionConfigComponent,
    WorkflowCrmActionConfigComponent,
    WorkflowEdgeConfigComponent
  ],
  templateUrl: './inspector-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowInspectorHostComponent {
  private readonly store = inject(WorkflowGraphStore);
  private readonly editor = inject(WorkflowEditorService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  protected readonly meta = WORKFLOW_NODE_TYPE_META;
  protected readonly readOnly = computed(() => this.editor.readOnly());
  protected readonly workflow = this.editor.workflow;

  protected readonly selection = this.store.selection;

  protected readonly selectedNode = computed<WorkflowGraphNode | null>(() => {
    const sel = this.selection();
    if (sel?.kind !== 'node') {
      return null;
    }
    // Se lee la lista para refrescar cuando el nodo cambia.
    this.store.nodes();
    return this.store.findNode(sel.nodeKey) ?? null;
  });

  protected readonly selectedEdgeId = computed<string | null>(() => {
    const sel = this.selection();
    return sel?.kind === 'edge' ? sel.edgeId : null;
  });

  /**
   * Ítem del catálogo del SUBTIPO seleccionado. Aporta la etiqueta y la descripción reales del
   * paso ("cuándo se dispara y qué hay que configurar"), que el mapa por TIPO
   * (`WORKFLOW_NODE_TYPE_META`) no puede dar: ahí "Disparador" es siempre "Inscribe registros que
   * cumplen el criterio", da igual que sea un evento del CRM o una inscripción manual.
   */
  protected readonly catalogItem = computed(() => {
    const node = this.selectedNode();
    return node ? (this.editor.catalogItem(node.nodeType, node.nodeSubtype ?? null) ?? null) : null;
  });

  /** Categoría funcional del subtipo de ACTION → elige el componente de config. */
  protected readonly actionCategory = computed(() => getWorkflowActionCategory(this.selectedNode()?.nodeSubtype));

  /** Errores de contrato del nodo seleccionado (los del badge "Incompleto"). */
  protected readonly nodeErrors = computed(() => {
    const node = this.selectedNode();
    return node ? (this.store.nodeErrors().get(node.nodeKey) ?? []) : [];
  });

  /** Total de nodos/conexiones para el resumen del estado vacío. */
  protected readonly nodeCount = computed(() => this.store.nodes().length);
  protected readonly edgeCount = computed(() => this.store.edges().length);
  protected readonly graphErrors = this.store.graphErrors;

  // ── Cabecera: nombre y activo ───────────────────────────────────────────────

  protected onTitleChange(event: Event): void {
    const node = this.selectedNode();
    if (!node) {
      return;
    }
    this.store.updateNode(node.nodeKey, { title: (event.target as HTMLInputElement).value });
    this.editor.queueAutosave();
  }

  protected onActiveChange(event: Event): void {
    const node = this.selectedNode();
    if (!node) {
      return;
    }
    this.store.updateNode(node.nodeKey, { isActive: (event.target as HTMLInputElement).checked });
    this.editor.queueAutosave();
  }

  // ── Acciones del nodo ───────────────────────────────────────────────────────

  protected async onDeleteNode(): Promise<void> {
    const node = this.selectedNode();
    if (this.readOnly() || !node) {
      return;
    }
    const connections = this.store.countConnections([node.nodeKey]);
    const warning = connections > 0 ? ` Se eliminarán también ${connections} conexión(es) asociada(s).` : '';
    const confirmed = await this.confirmDialog.confirmDanger({
      title: 'Eliminar paso',
      message: `¿Seguro que quieres eliminar este paso?${warning}`
    });
    if (!confirmed) {
      return;
    }
    this.store.removeNodes([node.nodeKey]);
    this.toast.deleted('Paso');
    this.editor.queueAutosave();
  }

  protected onDuplicateNode(): void {
    const node = this.selectedNode();
    if (this.readOnly() || !node) {
      return;
    }
    const created = this.store.duplicateNodes([node.nodeKey]);
    if (!created.length) {
      this.toast.warning({ title: 'No se duplicó', message: 'El disparador no se puede duplicar.' });
      return;
    }
    this.toast.created('Paso');
    this.editor.queueAutosave();
  }

  protected onDeselect(): void {
    this.store.setSelection(null);
  }

  // ── Escape hatch: editor JSON crudo ─────────────────────────────────────────

  protected readonly rawJsonDraft = signal<string | null>(null);
  protected readonly rawJsonError = signal('');

  protected readonly rawJson = computed(() => {
    const draft = this.rawJsonDraft();
    if (draft !== null) {
      return draft;
    }
    const node = this.selectedNode();
    return node ? JSON.stringify(node.config, null, 2) : '{}';
  });

  protected onRawJsonInput(value: string): void {
    this.rawJsonDraft.set(value);
    this.rawJsonError.set('');
  }

  protected applyRawJson(): void {
    const node = this.selectedNode();
    const draft = this.rawJsonDraft();
    if (this.readOnly() || !node || draft === null) {
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch {
      this.rawJsonError.set('El JSON no es válido.');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      this.rawJsonError.set('La configuración debe ser un objeto JSON.');
      return;
    }
    this.store.updateNodeConfig(node.nodeKey, parsed as Record<string, unknown>);
    this.editor.queueAutosave();
    this.rawJsonDraft.set(null);
    this.toast.updated('Configuración');
  }

  protected discardRawJson(): void {
    this.rawJsonDraft.set(null);
    this.rawJsonError.set('');
  }

  protected textareaValue(event: Event): string {
    return (event.target as HTMLTextAreaElement).value;
  }
}
